import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Time } from '../../util/Time'
import { useSessionInfo } from '../../util/RequireConfig'
import Markdown from '../../util/Markdown'
import { diffWords } from 'diff'

const notesQuery = gql`
  query AlertNotesQuery($id: Int!) {
    alert(id: $id) {
      id
      alertID
      notes {
        id
        alertID
        body
        createdAt
        updatedAt
        user {
          id
          name
        }
        reactions {
          emoji
          count
          hasReacted
          users {
            id
            name
          }
        }
        revisions {
          id
          body
          editedAt
          user {
            id
            name
          }
        }
      }
    }
  }
`

const addNoteMutation = gql`
  mutation AddAlertNote($input: AddAlertNoteInput!) {
    addAlertNote(input: $input) {
      id
    }
  }
`

const updateNoteMutation = gql`
  mutation UpdateAlertNote($input: UpdateAlertNoteInput!) {
    updateAlertNote(input: $input) {
      id
    }
  }
`

const deleteNoteMutation = gql`
  mutation DeleteAlertNote($id: ID!) {
    deleteAlertNote(id: $id)
  }
`

const toggleReactionMutation = gql`
  mutation ToggleAlertNoteReaction($noteID: ID!, $emoji: String!) {
    toggleAlertNoteReaction(noteID: $noteID, emoji: $emoji)
  }
`

interface AlertNotesProps {
  alertID: number
}

interface ReactionData {
  emoji: string
  count: number
  hasReacted: boolean
  users: { id: string; name: string }[]
}

interface RevisionData {
  id: string
  body: string
  editedAt: string
  user: { id: string; name: string }
}

interface NoteData {
  id: string
  alertID: number
  body: string
  createdAt: string
  updatedAt: string | null
  user: { id: string; name: string }
  reactions: ReactionData[]
  revisions: RevisionData[]
}

const EMOJI_MAP: Record<string, string> = {
  thumbs_up: '👍',
  thumbs_down: '👎',
  eyes: '👀',
  rocket: '🚀',
  heart: '❤️',
  tada: '🎉',
}

function DiffView({
  oldText,
  newText,
}: {
  oldText: string
  newText: string
}): React.JSX.Element {
  const changes = diffWords(oldText, newText)
  return (
    <Typography
      variant='body2'
      component='pre'
      sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', m: 0 }}
    >
      {changes.map((part, i) => {
        if (part.added) {
          return (
            <span key={i} style={{ backgroundColor: '#d4edda', color: '#155724' }}>
              {part.value}
            </span>
          )
        }
        if (part.removed) {
          return (
            <span key={i} style={{ backgroundColor: '#f8d7da', color: '#721c24', textDecoration: 'line-through' }}>
              {part.value}
            </span>
          )
        }
        return <span key={i}>{part.value}</span>
      })}
    </Typography>
  )
}

export default function AlertNotes(props: AlertNotesProps): React.JSX.Element {
  const { userID, isAdmin } = useSessionInfo()
  const [newBody, setNewBody] = useState('')
  const [editingID, setEditingID] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [deleteID, setDeleteID] = useState<string | null>(null)
  const [historyNoteID, setHistoryNoteID] = useState<string | null>(null)

  const { data, refetch } = useQuery(notesQuery, {
    variables: { id: props.alertID },
  })

  const [addNote] = useMutation(addNoteMutation)
  const [updateNote] = useMutation(updateNoteMutation)
  const [deleteNote] = useMutation(deleteNoteMutation)
  const [toggleReaction] = useMutation(toggleReactionMutation)

  const alertID = data?.alert?.alertID
  const notes: NoteData[] = data?.alert?.notes || []

  const handleAdd = (): void => {
    if (!newBody.trim()) return
    addNote({
      variables: { input: { alertID: props.alertID, body: newBody.trim() } },
    }).then(() => {
      setNewBody('')
      refetch()
    })
  }

  const handleUpdate = (id: string): void => {
    if (!editBody.trim()) return
    updateNote({
      variables: { input: { id, body: editBody.trim() } },
    }).then(() => {
      setEditingID(null)
      setEditBody('')
      refetch()
    })
  }

  const handleDelete = (): void => {
    if (!deleteID) return
    deleteNote({ variables: { id: deleteID } }).then(() => {
      setDeleteID(null)
      refetch()
    })
  }

  const handleToggleReaction = (noteID: string, emoji: string): void => {
    toggleReaction({ variables: { noteID, emoji } }).then(() => refetch())
  }

  const canEdit = (note: NoteData): boolean =>
    note.user.id === userID || isAdmin

  const historyNote = notes.find((n) => n.id === historyNoteID)

  return (
    <Card sx={{ width: '100%' }}>
      <CardHeader title='Notes' />
      <CardContent sx={{ pt: 0 }}>
        {notes.length === 0 && (
          <Typography variant='body2' color='textSecondary'>
            No notes yet. Add a note to provide context for this alert.
          </Typography>
        )}
        {notes.length > 0 && (
          <List disablePadding>
            {notes.map((note) => (
              <ListItem
                key={note.id}
                alignItems='flex-start'
                disableGutters
                sx={{ flexDirection: 'column', alignItems: 'stretch' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <ListItemText
                    sx={{ flex: 1 }}
                    primary={
                      editingID === note.id ? (
                        <Box sx={{ pr: 8 }}>
                          <TextField
                            fullWidth
                            multiline
                            size='small'
                            value={editBody}
                            onChange={(e) =>
                              setEditBody(
                                (e.target as unknown as { value: string }).value,
                              )
                            }
                            inputProps={{ maxLength: 4096 }}
                          />
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => handleUpdate(note.id)}
                              disabled={!editBody.trim()}
                            >
                              Save
                            </Button>
                            <Button
                              size='small'
                              sx={{ ml: 1 }}
                              onClick={() => setEditingID(null)}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ pr: 8 }}>
                          <Markdown value={note.body} />
                        </Box>
                      )
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          component='span'
                          variant='body2'
                          fontWeight='bold'
                          color='textPrimary'
                        >
                          {note.user.name}
                        </Typography>
                        {' — '}
                        <Time time={note.createdAt} format='relative' />
                        {note.updatedAt && (
                          <Typography
                            component='span'
                            variant='caption'
                            color='textSecondary'
                            sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 0.5 }}
                            onClick={() => setHistoryNoteID(note.id)}
                          >
                            (edited)
                          </Typography>
                        )}
                        {alertID && note.alertID !== alertID && (
                          <Chip
                            label='From previous alert'
                            size='small'
                            variant='outlined'
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </React.Fragment>
                    }
                  />
                  {canEdit(note) && editingID !== note.id && (
                    <Box sx={{ display: 'flex', mt: 1 }}>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setEditingID(note.id)
                          setEditBody(note.body)
                        }}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => setDeleteID(note.id)}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Reactions */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {note.reactions.map((r) => (
                    <Tooltip
                      key={r.emoji}
                      title={r.users.map((u) => u.name).join(', ')}
                    >
                      <Chip
                        size='small'
                        label={`${EMOJI_MAP[r.emoji]} ${r.count}`}
                        variant={r.hasReacted ? 'filled' : 'outlined'}
                        color={r.hasReacted ? 'primary' : 'default'}
                        onClick={() => handleToggleReaction(note.id, r.emoji)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  ))}
                  {/* Add reaction menu */}
                  {Object.entries(EMOJI_MAP)
                    .filter(([key]) => !note.reactions.some((r) => r.emoji === key))
                    .map(([key, display]) => (
                      <Chip
                        key={key}
                        size='small'
                        label={display}
                        variant='outlined'
                        onClick={() => handleToggleReaction(note.id, key)}
                        sx={{ cursor: 'pointer', opacity: 0.5, '&:hover': { opacity: 1 } }}
                      />
                    ))}
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            size='small'
            placeholder='Add a note... (supports Markdown)'
            value={newBody}
            onChange={(e) =>
              setNewBody((e.target as unknown as { value: string }).value)
            }
            inputProps={{ maxLength: 4096 }}
          />
          <Button
            variant='contained'
            onClick={handleAdd}
            disabled={!newBody.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>
      </CardContent>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteID} onClose={() => setDeleteID(null)}>
        <DialogContent>
          <DialogContentText>Delete this note?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteID(null)}>Cancel</Button>
          <Button onClick={handleDelete} color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit history dialog */}
      <Dialog
        open={!!historyNoteID}
        onClose={() => setHistoryNoteID(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Edit History</DialogTitle>
        <DialogContent>
          {historyNote && historyNote.revisions.length > 0 ? (
            <List disablePadding>
              {historyNote.revisions.map((rev, idx) => {
                const prevBody = idx === 0 ? '' : historyNote.revisions[idx - 1].body
                return (
                  <ListItem key={rev.id} disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <Typography variant='caption' color='textSecondary'>
                      {idx === 0 ? 'Original version' : `Revision ${idx}`} by{' '}
                      <strong>{rev.user.name}</strong> —{' '}
                      <Time time={rev.editedAt} format='relative' />
                    </Typography>
                    {idx === 0 ? (
                      <Typography
                        variant='body2'
                        component='pre'
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', m: 0, mt: 0.5 }}
                      >
                        {rev.body}
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        <DiffView oldText={prevBody} newText={rev.body} />
                      </Box>
                    )}
                  </ListItem>
                )
              })}
              {/* Current version diff */}
              <ListItem disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <Typography variant='caption' color='textSecondary'>
                  Current version
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <DiffView
                    oldText={historyNote.revisions[historyNote.revisions.length - 1].body}
                    newText={historyNote.body}
                  />
                </Box>
              </ListItem>
            </List>
          ) : (
            <Typography variant='body2' color='textSecondary'>
              No revision history available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryNoteID(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
